import { Badge } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useMemo } from "react";
import { BaseTableLayout, ColumnDef } from "../../components/base-table-layout";
import { HighlightedText } from "../../components/highlighted-text";
import { TableRow } from "../../utils/tables-metadata";
import { CareGapRowData } from "./care-gaps-rows";

export type CareGapsTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  isRowViewed?: (row: TableRow) => boolean;
};

/**
 * Table component for displaying care gaps.
 * Care gaps are quality measures that track gaps in care for patients.
 */
export function CareGapsTable({ rows, onRowClick, filterQuery, isRowViewed }: CareGapsTableProps) {
  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  const columns: ColumnDef<CareGapRowData>[] = useMemo(
    () => [
      { key: "formattedMeasureName", header: "Measure Name" },
      {
        key: "status",
        header: "Status",
        render: (row, searchQuery) => (
          <Badge colorPalette={row.statusColor} variant="subtle" size="sm">
            <HighlightedText text={row.formattedStatus} searchQuery={searchQuery} />
          </Badge>
        ),
      },
      { key: "period", header: "Period", isHiddenOnSmallScreen: true },
      { key: "formattedLastRun", header: "Last Run", isHiddenOnSmallScreen: true },
    ],
    []
  );

  return (
    <BaseTableLayout
      title="Care Gaps"
      rows={rows as unknown as CareGapRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
    />
  );
}
